import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import type { RegistrationCollectionResponse } from '@entities/registration';

type BaseOperation = operations['getApiV1CoursesByCourseRef'];
type MenteesOperation = operations['getApiV1UserProfilesByProfileRefMentees'];

export type CrmCourseProfileBaseResponse =
  BaseOperation['responses'][200]['content']['application/json'];
export type CrmCourseProfileRegistrationsResponse = RegistrationCollectionResponse;
type CrmCourseProfileMenteesTransportResponse =
  MenteesOperation['responses'][200]['content']['application/json'];

export const crmCourseProfileTabSchema = z.enum([
  'general',
  'registrations',
  'students',
  'materials',
  'completion-form',
  'team',
  'marketplace',
]);
export type CrmCourseProfileTab = z.infer<typeof crmCourseProfileTabSchema>;

export const crmCourseProfilePeopleViewSchema = z.enum(['students', 'mentees']);
export type CrmCourseProfilePeopleView = z.infer<typeof crmCourseProfilePeopleViewSchema>;

export const crmCourseProfileCollectionSchema = z.enum([
  'registrations',
  'students',
  'mentees',
  'materials',
  'completion-form',
  'team',
]);
export type CrmCourseProfileCollection = z.infer<typeof crmCourseProfileCollectionSchema>;

export const crmCourseProfileCertificationStateSchema = z.enum(['Certified', 'To certify']);
export type CrmCourseProfileCertificationState = z.infer<
  typeof crmCourseProfileCertificationStateSchema
>;

/** Course-scoped subset of the canonical Registration list filters. */
export type CrmCourseProfileRegistrationFilters = {
  certifiedFrom?: string;
  certifiedTo?: string;
  costMax?: number;
  costMin?: number;
  instructorPaymentStatus?: string;
  registrationReferenceType?: string;
  registrationType?: string;
  studentPaymentStatus?: string;
  submittedFrom?: string;
  submittedTo?: string;
};

export const crmCourseProfileRegistrationSortSchema = z.enum([
  'registrationCertifiedAt.asc',
  'registrationCertifiedAt.desc',
  'registrationNumber.asc',
  'registrationNumber.desc',
  'registrationSubmittedAt.asc',
  'registrationSubmittedAt.desc',
]);
export type CrmCourseProfileRegistrationSort = z.infer<
  typeof crmCourseProfileRegistrationSortSchema
>;

export const crmCourseProfileStudentSortSchema = z.enum(['studentName.asc', 'studentName.desc']);
export const crmCourseProfileSortSchema = z.union([
  crmCourseProfileRegistrationSortSchema,
  crmCourseProfileStudentSortSchema,
]);
export type CrmCourseProfileSort = z.infer<typeof crmCourseProfileSortSchema>;

const nullableText = z.string().nullable();
const uuid = z.uuid();
const relatedRecordSchema = z.object({ id: uuid, name: nullableText });
const courseTypeRelationSchema = relatedRecordSchema.extend({ badgeImageUrl: nullableText });
const userProfileSummarySchema = relatedRecordSchema.extend({
  avatarUrl: nullableText,
  email: nullableText.optional(),
});

export const crmCourseProfileBaseResponseSchema = z.object({
  data: z.object({
    administration: z.object({
      createdAt: nullableText,
      creator: relatedRecordSchema.nullable(),
      issueGuidance: nullableText,
      logistics: nullableText,
      organizationGuidance: nullableText,
      seaConditions: nullableText,
      sku: nullableText,
      submittedAt: nullableText,
      updatedAt: nullableText,
      superAdminNote: nullableText.optional(),
    }),
    country: nullableText,
    completionRequirementStats: z.array(
      z.object({
        completedRegistrations: z.number().int().nonnegative(),
        label: nullableText,
        order: z.number().int().positive(),
        requirementId: uuid,
        totalRegistrations: z.number().int().nonnegative(),
      })
    ),
    freeAccess: z.object({ roles: z.array(z.string()), users: z.array(userProfileSummarySchema) }),
    number: nullableText,
    paymentPermission: nullableText,
    registrationPermission: nullableText,
    poolAccess: nullableText,
    waterTemperature: nullableText,
    weather: nullableText,
    team: z.object({
      assistants: z.array(userProfileSummarySchema),
      coInstructors: z.array(userProfileSummarySchema),
      creator: userProfileSummarySchema.nullable(),
      invitedGuests: z.array(userProfileSummarySchema),
      leadInstructor: userProfileSummarySchema.nullable(),
    }),
    marketplace: z.object({
      assets: z.array(z.object({ kind: z.enum(['cover16x9', 'cover1x1', 'cover4x1']) })),
      featured: z.boolean(),
    }),
    capacity: z.object({
      percentTaken: z.number().nullable(),
      spotsTaken: z.number().int().nonnegative(),
      total: z.number().nullable(),
    }),
    courseRef: uuid,
    description: nullableText,
    endAt: nullableText,
    format: nullableText,
    language: nullableText,
    length: nullableText,
    location: nullableText,
    school: relatedRecordSchema.nullable(),
    startAt: nullableText,
    status: nullableText,
    title: nullableText,
    courseType: courseTypeRelationSchema.nullable(),
  }),
}) satisfies z.ZodType<CrmCourseProfileBaseResponse>;

const crmCourseProfileMenteesTransportSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        avatarUrl: nullableText,
        btLevel: nullableText,
        canOpenProfile: z.boolean(),
        completedFreediverChallenges: z.number().int().nonnegative(),
        completedWorkouts: z.number().int().nonnegative(),
        firstName: nullableText,
        id: uuid,
        lastName: nullableText,
      })
    ),
  }),
  meta: z.object({
    hasNextPage: z.boolean(),
    nextCursor: nullableText,
    pageSize: z.literal(20),
    totalItems: z.number().int().nonnegative(),
  }),
}) satisfies z.ZodType<CrmCourseProfileMenteesTransportResponse>;

/** Adapts the generated relationship transport to the retained people presentation. */
export const crmCourseProfileMenteesResponseSchema =
  crmCourseProfileMenteesTransportSchema.transform((response) => ({
    data: {
      courseRef: '',
      rows: response.data.items.map((item) => ({
        avatarUrl: item.avatarUrl,
        completedFreediverChallenges: item.completedFreediverChallenges,
        completedWorkouts: item.completedWorkouts,
        baseLevels: item.btLevel === null ? [] : [item.btLevel],
        id: item.id,
        name:
          [item.firstName, item.lastName]
            .filter((value): value is string => value !== null)
            .join(' ') || null,
        profileHref: item.canOpenProfile ? `/crm/user-profiles/${item.id}` : null,
      })),
      section: 'mentees' as const,
    },
    meta: response.meta,
  }));
export type CrmCourseProfileMenteesResponse = z.infer<typeof crmCourseProfileMenteesResponseSchema>;
const crmCourseProfileStudentsTransportSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        avatarUrl: nullableText,
        baseLevels: z.array(z.string()),
        canOpenProfile: z.boolean(),
        completedFreediverChallenges: z.number().int().nonnegative(),
        completedWorkouts: z.number().int().nonnegative(),
        firstName: nullableText,
        id: uuid,
        lastName: nullableText,
      })
    ),
  }),
  meta: z.object({
    hasNextPage: z.boolean(),
    nextCursor: nullableText,
    pageSize: z.literal(20),
    totalItems: z.number().int().nonnegative(),
  }),
});

/** Course Students and Mentees both present every User Base Level without reducing the array. */
export const crmCourseProfileStudentsResponseSchema =
  crmCourseProfileStudentsTransportSchema.transform((response) => ({
    data: {
      courseRef: '',
      rows: response.data.items.map((item) => ({
        avatarUrl: item.avatarUrl,
        baseLevels: item.baseLevels,
        completedFreediverChallenges: item.completedFreediverChallenges,
        completedWorkouts: item.completedWorkouts,
        id: item.id,
        name:
          [item.firstName, item.lastName]
            .filter((value): value is string => value !== null)
            .join(' ') || null,
        profileHref: item.canOpenProfile ? `/crm/user-profiles/${item.id}` : null,
      })),
      section: 'students' as const,
    },
    meta: response.meta,
  }));
export type CrmCourseProfileStudentsResponse = z.infer<
  typeof crmCourseProfileStudentsResponseSchema
>;
export type CrmCourseProfileMarketplaceResponse = {
  data: CrmCourseProfileBaseResponse['data']['marketplace'];
};

export function courseTeamPresentation(response: CrmCourseProfileBaseResponse) {
  const { team, school } = response.data;
  return [
    ...(team.leadInstructor ? [{ ...team.leadInstructor, role: 'leadInstructor' as const }] : []),
    ...team.coInstructors.map((member) => ({ ...member, role: 'coInstructor' as const })),
    ...team.assistants.map((member) => ({ ...member, role: 'assistant' as const })),
    ...team.invitedGuests.map((member) => ({ ...member, role: 'invitedGuest' as const })),
    ...(school ? [{ ...school, avatarUrl: null, email: undefined, role: 'school' as const }] : []),
  ];
}
export type CrmCourseProfileTeamRow = ReturnType<typeof courseTeamPresentation>[number];

export type CourseTypeResponse =
  operations['getApiV1CourseTypesByCourseTypeId']['responses'][200]['content']['application/json'];
const completionFormRequirementSchema = z.object({
  id: uuid,
  name: nullableText,
  category: nullableText,
  type: nullableText,
  status: nullableText,
  certificationTypeIds: z.array(uuid).max(100),
  necessaryForCertificationTypeIds: z.array(uuid).max(100),
});
export const courseTypeResponseSchema = z.object({
  data: z.object({
    id: uuid,
    badgeImageUrl: nullableText,
    label: z.string().min(1),
    certificationTypes: z.array(z.object({ id: uuid, label: z.string().min(1) })).max(100),
    completionRequirements: z
      .array(z.object({ id: uuid, label: nullableText, order: z.number().int().positive() }))
      .max(100),
    completionFormRequirements: z.array(completionFormRequirementSchema).max(100),
  }),
}) satisfies z.ZodType<CourseTypeResponse>;

export type CrmCourseProfileMaterialsResponse =
  operations['getApiV1Materials']['responses'][200]['content']['application/json'];

const materialTextFields = {
  name: nullableText,
  description: nullableText,
  category: nullableText,
  language: nullableText,
  mediaType: nullableText,
};
const assignmentSchema = z.discriminatedUnion('role', [
  z.object({ role: z.literal('main') }).strict(),
  z.object({ role: z.literal('additional'), position: z.number().int().positive() }).strict(),
]);
const educationModuleSummarySchema = z.object({
  id: uuid,
  name: nullableText,
  language: nullableText,
  publicationStatus: nullableText,
  maximumAttempts: z.number().int().positive().nullable(),
});
const onlineMaterialFields = {
  educationModule: educationModuleSummarySchema,
  assignments: z.array(assignmentSchema).min(1).max(200),
};
const safeExternalUrl = z.url().refine((url) => {
  const protocol = new URL(url).protocol;
  return protocol === 'https:' || protocol === 'http:';
});
const materialListItemSchema = z.discriminatedUnion('kind', [
  z.object({
    id: uuid,
    kind: z.literal('file'),
    ...materialTextFields,
    availableActions: z.array(z.enum(['preview', 'download'])).max(2),
  }),
  z.object({ id: uuid, kind: z.literal('link'), ...materialTextFields, url: safeExternalUrl }),
  z.object({ id: uuid, kind: z.literal('youtube'), ...materialTextFields, url: safeExternalUrl }),
  z.object({ id: uuid, kind: z.literal('online_course'), ...onlineMaterialFields }),
  z.object({ id: uuid, kind: z.literal('online_exam'), ...onlineMaterialFields }),
]);
export const crmCourseProfileMaterialsResponseSchema = z.object({
  data: z.object({ items: z.array(materialListItemSchema).max(100) }),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().min(1).max(100),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
}) satisfies z.ZodType<CrmCourseProfileMaterialsResponse>;
